import React, { useCallback, useEffect, useState } from 'react';
import { 
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Popover,
  Button,
  Chip,
  TablePagination
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import dayjs from 'dayjs';
import apiClient from '../../../utils/axiosClient';
import { exportToExcel } from '../../../utils/excelExport';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';

import type { RCSettlementSummary } from '../../../types/settlement.types';

interface RCSettlementDashboardProps {
  year: number;
  month: number;
}

const RCSettlementDashboard: React.FC<RCSettlementDashboardProps> = ({ year, month }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [rcSettlementSummary, setRcSettlementSummary] = useState<RCSettlementSummary>([]);
  const [selectedItem, setSelectedItem] = useState<{date: string, rcType: string} | null>(null);
  const [sortField, setSortField] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [filterColumn, setFilterColumn] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [rcTypeFilter, setRcTypeFilter] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [excelDownloading, setExcelDownloading] = useState<boolean>(false);

  const rowsPerPage = 12;

  const startDate = dayjs().year(year).month(month - 1).startOf('month').format('YYYY-MM-DD');
  const endDate = dayjs().year(year).month(month - 1).endOf('month').format('YYYY-MM-DD');

  const fetchRCSettlementSummary = useCallback(() => {
    if (!year || !month) return;
    setLoading(true);

    apiClient.get(`/api/v1/settle/support/rc/summary`,{ params: { start_date: startDate, end_date: endDate } })
      .then((response) => {
        const data = response.data;
        if (!data || data.length === 0) {
          setErrorMessage('데이터가 존재하지 않습니다.');
        } else {
          setRcSettlementSummary(data);
        }
      })
      .catch((error) => {
        setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, year, month]);

  useEffect(() => {
    fetchRCSettlementSummary();
  }, [fetchRCSettlementSummary]);

  // 정렬 핸들러
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // 필터 팝오버 열기
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, column: string) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterColumn(column);
  };

  // 필터 팝오버 닫기
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setFilterColumn('');
  };

  // 페이지네이션 핸들러
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // 날짜별, RC 타입별 집계 데이터 생성 (필터링 및 정렬 적용)
  const getRcTypeSummary = () => {
    const summary = new Map();
    
    rcSettlementSummary.forEach(item => {
      const date = dayjs(item.actionDateTime).format('YYYY-MM-DD');
      const key = `${date}_${item.rcType}`;
      
      // 필터링 조건 확인
      const matchesDateFilter = !dateFilter || date.includes(dateFilter);
      const matchesRcTypeFilter = !rcTypeFilter || item.rcType.toLowerCase().includes(rcTypeFilter.toLowerCase());
      
      if (!matchesDateFilter || !matchesRcTypeFilter) return;
      
      if (!summary.has(key)) {
        summary.set(key, {
          date: date,
          rcType: item.rcType,
          totalClick: 0,
          totalPostbackCount: 0,
          totalPostbackSuccessCount: 0
        });
      }
      
      const existing = summary.get(key);
      existing.totalClick += item.click;
      existing.totalPostbackCount += item.postbackCount;
      existing.totalPostbackSuccessCount += item.postbackSuccessCount;
    });
    
    const sortedData = Array.from(summary.values()).sort((a, b) => {
      // 먼저 날짜 최신순(내림차순)으로 정렬
      const dateComparison = b.date.localeCompare(a.date);
      if (dateComparison !== 0) {
        return dateComparison;
      }
      
      // 같은 날짜 내에서는 Click 큰 순서대로 정렬
      return b.totalClick - a.totalClick;
    });
    
    return sortedData;
  };

  // 페이지네이션된 데이터 반환
  const getPaginatedData = () => {
    const allData = getRcTypeSummary();
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return allData.slice(startIndex, endIndex);
  };

  // RC 타입별 통합 Click 수 계산 (그래프용)
  const getRcTypeClickData = () => {
    const clickSummary = new Map();
    
    rcSettlementSummary.forEach(item => {
      const rcType = item.rcType;
      if (!clickSummary.has(rcType)) {
        clickSummary.set(rcType, {
          rcType: rcType,
          totalClick: 0
        });
      }
      
      const existing = clickSummary.get(rcType);
      existing.totalClick += item.click;
    });
    
    const result = Array.from(clickSummary.values())
      .sort((a, b) => a.totalClick - b.totalClick) // 왼쪽에서 오른쪽으로 증가
      .map((item, index) => ({
        rcType: item.rcType,
        click: item.totalClick,
        color: getPastelColor(index)
      }));
    
    return result;
  };

  // 눈이 편안한 색상 생성 함수
  const getPastelColor = (index: number) => {
    const colors = [
      '#6B73FF', // 소프트 블루
      '#9B59B6', // 소프트 퍼플
      '#2ECC71', // 소프트 그린
      '#F39C12', // 소프트 오렌지
      '#E74C3C', // 소프트 레드
      '#1ABC9C', // 소프트 터콰이즈
      '#3498DB', // 소프트 스카이 블루
      '#E67E22', // 소프트 오렌지
      '#8E44AD', // 소프트 바이올렛
      '#16A085', // 소프트 틸
      '#27AE60', // 소프트 에메랄드
      '#D35400', // 소프트 브라운
      '#2980B9', // 소프트 블루
      '#7F8C8D', // 소프트 그레이
      '#C0392B', // 소프트 마룬
      '#8E44AD', // 소프트 퍼플
      '#27AE60', // 소프트 그린
      '#D35400', // 소프트 오렌지
      '#2980B9', // 소프트 블루
      '#7F8C8D', // 소프트 그레이
      '#C0392B', // 소프트 마룬
      '#8E44AD', // 소프트 퍼플
      '#27AE60', // 소프트 그린
    ];
    
    return colors[index % colors.length];
  };

  // 선택된 날짜와 RC 타입의 CP 타입별 상세 데이터
  const getCpTypeDetails = () => {
    if (!selectedItem) return [];
    return getCpTypeDetailsFor(selectedItem.date, selectedItem.rcType);
  };

  // (날짜, RC타입)에 대한 CP 타입별 상세 - 엑셀 다운로드용
  const getCpTypeDetailsFor = (date: string, rcType: string) => {
    const details = new Map<string, { cpType: string; totalClick: number; totalPostbackCount: number; totalPostbackSuccessCount: number }>();

    rcSettlementSummary
      .filter(
        (item) =>
          dayjs(item.actionDateTime).format('YYYY-MM-DD') === date && item.rcType === rcType
      )
      .forEach((item) => {
        const key = item.cpType;
        if (!details.has(key)) {
          details.set(key, {
            cpType: key,
            totalClick: 0,
            totalPostbackCount: 0,
            totalPostbackSuccessCount: 0,
          });
        }
        const existing = details.get(key);
        if (existing) {
          existing.totalClick += item.click;
          existing.totalPostbackCount += item.postbackCount;
          existing.totalPostbackSuccessCount += item.postbackSuccessCount;
        }
      });

    return Array.from(details.values()).sort((a, b) => b.totalClick - a.totalClick);
  };

  const handleDownloadExcel = () => {
    setExcelDownloading(true);
    apiClient
      .get('/api/v1/settle/support/rc/summary/excel', {
        params: { start_date: startDate, end_date: endDate },
      })
      .then((response) => {
        const data = response.data as Array<{
          appID: string;
          actionDateTime: string;
          rcType: string;
          cpType: string;
          click: number;
          postback: number;
          postbackSuccess: number;
        }>;
        const headers = ['날짜', 'appID', 'RC 타입', 'CP 타입', 'Click', 'Postback', 'Postback 성공'];
        const rows: (string | number)[][] = [
          headers,
          ...data.map((row) => [
            row.actionDateTime,
            row.appID,
            row.rcType,
            row.cpType,
            row.click,
            row.postback,
            row.postbackSuccess,
          ]),
        ];
        const filename = `${year}년_${month}월_RC현황.xlsx`;
        exportToExcel(rows, filename, 'RC 현황');
      })
      .catch(() => {
        setErrorMessage('엑셀 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
      })
      .finally(() => setExcelDownloading(false));
  };

  // 로딩 화면(전체 블랭크 + 스피너)
  if (loading) {
    return (
      <Box
        sx={{
          opacity: loading ? 0.3 : 1,
          transition: 'opacity 0.3s ease',
          minHeight: '100vh'
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress size={50} sx={{ color: '#0044D2' }} />
          <Typography variant="body1" sx={{ ml: 2 }}>데이터 로딩 중...</Typography>
        </Box>
      </Box>
    );
  }

  // 에러 화면(전체 블랭크 + 에러)
  if (errorMessage) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* 메인 테이블 - 날짜별, RC 타입별 집계 */}
      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, ml: 1, mt: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {year}년 {month}월 RC 현황
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDownloadExcel}
            disabled={excelDownloading}
            sx={{ textTransform: 'none' }}
          >
            {excelDownloading ? '다운로드 중...' : '엑셀 다운로드'}
          </Button>
        </Box>
        <TableContainer sx={{ mb: 3 }}>
          <Table>
             <TableHead>
               <TableRow>
                 <TableCell sx={{ fontWeight: 'bold' }}>
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <TableSortLabel
                       active={sortField === 'date'}
                       direction={sortField === 'date' ? sortDirection : 'asc'}
                       onClick={() => handleSort('date')}
                     >
                       날짜
                     </TableSortLabel>
                     <Button
                       size="small"
                       onClick={(e) => handleFilterClick(e, 'date')}
                       sx={{ minWidth: 'auto', p: 0.5 }}
                     >
                       🔍
                     </Button>
                     {dateFilter && (
                       <Chip
                         label={`날짜: ${dateFilter}`}
                         size="small"
                         onDelete={() => setDateFilter('')}
                         sx={{ ml: 1 }}
                       />
                     )}
                   </Box>
                 </TableCell>
                 <TableCell sx={{ fontWeight: 'bold' }}>
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <TableSortLabel
                       active={sortField === 'rcType'}
                       direction={sortField === 'rcType' ? sortDirection : 'asc'}
                       onClick={() => handleSort('rcType')}
                     >
                       RC 타입
                     </TableSortLabel>
                     <Button
                       size="small"
                       onClick={(e) => handleFilterClick(e, 'rcType')}
                       sx={{ minWidth: 'auto', p: 0.5 }}
                     >
                       🔍
                     </Button>
                     {rcTypeFilter && (
                       <Chip
                         label={`RC: ${rcTypeFilter}`}
                         size="small"
                         onDelete={() => setRcTypeFilter('')}
                         sx={{ ml: 1 }}
                       />
                     )}
                   </Box>
                 </TableCell>
                 <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                   <TableSortLabel
                     active={sortField === 'totalClick'}
                     direction={sortField === 'totalClick' ? sortDirection : 'asc'}
                     onClick={() => handleSort('totalClick')}
                   >
                     Click
                   </TableSortLabel>
                 </TableCell>
                 <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                   <TableSortLabel
                     active={sortField === 'totalPostbackCount'}
                     direction={sortField === 'totalPostbackCount' ? sortDirection : 'asc'}
                     onClick={() => handleSort('totalPostbackCount')}
                   >
                     Postback
                   </TableSortLabel>
                 </TableCell>
                 <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                   <TableSortLabel
                     active={sortField === 'totalPostbackSuccessCount'}
                     direction={sortField === 'totalPostbackSuccessCount' ? sortDirection : 'asc'}
                     onClick={() => handleSort('totalPostbackSuccessCount')}
                   >
                     Postback 성공
                   </TableSortLabel>
                 </TableCell>
               </TableRow>
             </TableHead>
            <TableBody>
              {getPaginatedData().map((row) => (
                <React.Fragment key={`${row.date}_${row.rcType}`}>
                  <TableRow 
                    hover
                    onClick={() => setSelectedItem(
                      selectedItem?.date === row.date && selectedItem?.rcType === row.rcType 
                        ? null 
                        : {date: row.date, rcType: row.rcType}
                    )}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: selectedItem?.date === row.date && selectedItem?.rcType === row.rcType ? '#e3f2fd' : 'inherit'
                    }}
                  >
                    <TableCell>{row.date}</TableCell>
                    <TableCell>{row.rcType}</TableCell>
                    <TableCell align="right">{row.totalClick.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.totalPostbackCount.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.totalPostbackSuccessCount.toLocaleString()}</TableCell>
                  </TableRow>
                  
                  {/* 상세 테이블 - 선택된 행 바로 아래에 표시 */}
                  {selectedItem?.date === row.date && selectedItem?.rcType === row.rcType && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                        <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>CP 타입</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Click</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Postback</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Postback 성공</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {getCpTypeDetails().map((detailRow) => (
                                <TableRow key={detailRow.cpType}>
                                  <TableCell>{detailRow.cpType}</TableCell>
                                  <TableCell align="right">{detailRow.totalClick.toLocaleString()}</TableCell>
                                  <TableCell align="right">{detailRow.totalPostbackCount.toLocaleString()}</TableCell>
                                  <TableCell align="right">{detailRow.totalPostbackSuccessCount.toLocaleString()}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
         </TableContainer>
         
         {/* 페이지네이션 */}
         <TablePagination
           component="div"
           count={getRcTypeSummary().length}
           page={page}
           onPageChange={handleChangePage}
           rowsPerPage={rowsPerPage}
           rowsPerPageOptions={[rowsPerPage]}
         />
       </Box>

       {/* RC 타입별 Click 수 가로 막대 그래프 */}
       <Box
         sx={{
           ...PAGE_CARD_SX,
           width: '100%',
           maxWidth: '100%',
         }}
       >
         <Typography
           variant="h6"
           sx={{ fontWeight: 'bold', ml: 1, mt: 1, mb: 3 }}
         >
           RC 타입별 통합 Click 수 현황
         </Typography>
         <ResponsiveContainer width="100%" height={600}>
           <BarChart
             data={getRcTypeClickData()}
             margin={{
               top: 20,
               right: 30,
               left: 20,
               bottom: 40
             }}
           >
             <CartesianGrid strokeDasharray="3 3" />
             <XAxis 
               dataKey="rcType"
               angle={-45}
               textAnchor="end"
               height={100}
               fontSize={12}
             />
             <YAxis 
               tickFormatter={(value) => value.toLocaleString()}
               fontSize={12}
             />
             <Tooltip 
               formatter={(value: number) => [value.toLocaleString(), 'Click']}
               labelFormatter={(label) => `RC 타입: ${label}`}
             />
             <Bar 
               dataKey="click" 
               radius={[4, 4, 0, 0]}
             >
               {getRcTypeClickData().map((entry, index) => (
                 <Cell key={`cell-${index}`} fill={entry.color} />
               ))}
             </Bar>
           </BarChart>
         </ResponsiveContainer>
       </Box>

       {/* 필터 팝오버 */}
       <Popover
         open={Boolean(filterAnchorEl)}
         anchorEl={filterAnchorEl}
         onClose={handleFilterClose}
         anchorOrigin={{
           vertical: 'bottom',
           horizontal: 'left',
         }}
       >
         <Box sx={{ p: 2, minWidth: 200 }}>
           <Typography variant="subtitle2" gutterBottom>
             {filterColumn === 'date' ? '날짜 필터' : 'RC 타입 필터'}
           </Typography>
           <TextField
             fullWidth
             size="small"
             placeholder={filterColumn === 'date' ? '예: 2024-12' : 'RC 타입 검색'}
             value={filterColumn === 'date' ? dateFilter : rcTypeFilter}
             onChange={(e) => {
               if (filterColumn === 'date') {
                 setDateFilter(e.target.value);
               } else {
                 setRcTypeFilter(e.target.value);
               }
             }}
             sx={{ mb: 2 }}
           />
           <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
             <Button size="small" onClick={handleFilterClose}>
               취소
             </Button>
             <Button size="small" variant="contained" onClick={handleFilterClose}>
               적용
             </Button>
           </Box>
         </Box>
       </Popover>
     </Box>
  );
};

export default RCSettlementDashboard;
